import _ from "lodash";
import PropTypes from "prop-types";
import styled from "styled-components";

import GroupLabel from "../GroupLabel";
import GroupList from "../GroupList";
import GroupListItem from "../GroupListItem";
import Hint from "../Hint";
import OptionsButton from "../OptionsButton";
import StyledItemMarker from "../StyledItemMarker";
import { GroupActions } from ".";

const groupColorFallback = "darkgray";
const defaultGroupName = "Default";

const getGroupColor = (group) =>
    _.isNil(group.color) || group.color === ""
        ? groupColorFallback
        : group.color;

const getLightCount = (group, lights) =>
    [...lights].reduce((count, light) => {
        const lightGroupId = light.options?.controlGroupId;
        return !_.isNil(lightGroupId) && lightGroupId === group.groupID
            ? count + 1
            : count;
    }, 0);

const GroupName = styled.div`
    flex-grow: 1;
`;

/**
 * Sorted group list with actions, light counts, and per-group options.
 *
 * @param root0 - The component props.
 * @param root0.onAdd - Callback to add a new group.
 * @param root0.onPublishAll - Callback to publish all groups.
 * @param root0.onDownloadFloorspaceFile - Callback to download the floorspace file.
 * @param root0.floorspaceFileUrl - The URL for downloading the floorspace file.
 * @param root0.lightGroups - The light groups to display.
 * @param root0.lights - The lights map for count display.
 * @param root0.onSelect - Callback when a group is selected.
 * @param root0.onStartDimTest - Callback to start a dim test.
 * @param root0.onOpenSettings - Callback to open group settings.
 * @returns The rendered group list.
 */
const SelectorGroupList = ({
    onAdd,
    onPublishAll,
    onDownloadFloorspaceFile,
    floorspaceFileUrl,
    lightGroups,
    lights,
    onSelect,
    onStartDimTest,
    onOpenSettings,
}) => (
    <>
        <GroupActions
            onAdd={onAdd}
            onPublishAll={onPublishAll}
            onDownloadFloorspaceFile={onDownloadFloorspaceFile}
            floorspaceFileUrl={floorspaceFileUrl}
        />
        <GroupList>
            {lightGroups
                .sort((a, b) => a.displayID - b.displayID)
                .map((group) => {
                    const defaultGroup = group.displayID === 1;

                    return (
                        <GroupListItem key={group.displayID}>
                            <StyledItemMarker $color={getGroupColor(group)} />
                            <GroupLabel>
                                <GroupName>
                                    {group.groupName}{" "}
                                    <Hint>
                                        {defaultGroup ? defaultGroupName : ""}
                                    </Hint>
                                </GroupName>
                                <div>{getLightCount(group, lights)}</div>
                                <OptionsButton
                                    onEditGeometry={() => onSelect(group)}
                                    onStartDimTest={() => onStartDimTest(group)}
                                    dimTestsDisabled={!group.groupID}
                                    settingsDisabled={!group.groupID}
                                    onOpenSettings={() => onOpenSettings(group)}
                                    geometryEditable={!defaultGroup}
                                />
                            </GroupLabel>
                        </GroupListItem>
                    );
                })}
        </GroupList>
    </>
);

SelectorGroupList.propTypes = {
    onAdd: PropTypes.func.isRequired,
    onPublishAll: PropTypes.func.isRequired,
    onDownloadFloorspaceFile: PropTypes.func.isRequired,
    floorspaceFileUrl: PropTypes.string,
    lightGroups: PropTypes.array.isRequired,
    lights: PropTypes.array.isRequired,
    onSelect: PropTypes.func.isRequired,
    onStartDimTest: PropTypes.func.isRequired,
    onOpenSettings: PropTypes.func.isRequired,
};

export default SelectorGroupList;
